import { Component, inject, Input, TemplateRef, ViewChild } from "@angular/core";

import { DialogRef, DialogService } from "@progress/kendo-angular-dialog";
import { GridComponent, GroupableSettings, PagerSettings, SortSettings } from "@progress/kendo-angular-grid";
import { AggregateDescriptor, State } from "@progress/kendo-data-query";
import { FilterExpression } from "@progress/kendo-angular-filter";

import { PrimeNGConfig } from "primeng/api";
import { ToastrService } from "ngx-toastr";
import { BASE_URL } from "@jwt";

import { IGridToken } from "./grid-token";
import { IHelperModel } from "./helper-model";
import { IPersistStateModel } from "./persist-state-model";
import { PersistModelDialogComponent } from "../components/persist-model-dialog.component";
import { PersistListDialogComponent } from "../components/persist-list-dialog.component";
import { ColumnSettings } from "./column-settings.interface";
import { GridConfig } from "./grid-config.interface";
import { PersistStateService } from "../../../services/persist-state.service";

const getCircularReplacer = () => {
    const seen = new WeakSet();
    return (_key: any, value: any) => {
        if (typeof value === "object" && value !== null) {
            if (seen.has(value)) {
                return;
            }
            seen.add(value);
        }
        return value;
    };
};

@Component({
    template: ''
})
export abstract class BaseGridComponent implements IGridToken {
    @ViewChild("grid") public grid!: GridComponent;
    @Input() public loading = false;
    @Input() aggregatable = false;
    @Input() aggregateDescriptors: AggregateDescriptor[];
    @Input() aggregateTemplates!: { [key: string]: TemplateRef<any> }; // Dynamic templates
    @Input() groupable: GroupableSettings;
    @Input() sortable: SortSettings = { allowUnsort: false, mode: 'single' };

    @Input() pageTitleEnable: boolean = true;
    pageTitle: string;

    abstract mapGridConfig(): void;
    abstract getDataSource(): Promise<void>;

    get selectedKeys() {
        return this._selectedKeys;
    }

    set selectedKeys(value: number[]) {
        this._selectedKeys = value;
    }

    private _selectedKeys: number[] = [];

    protected helperModel: IHelperModel;
    protected pager: PagerSettings;
    protected state: State = {
        skip: 0,
        take: 100,
        group: [],
        filter: { filters: [], logic: "or" },
        sort: [],
    };
    protected columns: any[];
    protected filterExpressions: FilterExpression[];

    persistStateService = inject(PersistStateService);

    constructor(
        protected primengConfig: PrimeNGConfig,
        protected toastrService: ToastrService,
        protected dialogService: DialogService) {
    }

    getSelectedKeys() {
        return this._selectedKeys;
    }

    reloadData(): Promise<void> {
        return this.getDataSource();
    }

    clearSelectedKeys(): void {
        this.selectedKeys = [];
    }

    openPersistModelDialog(id: number = 0, name: string = "", primary: boolean = false, displayOrder: number = 0) {

        const dialogRef: DialogRef = this.dialogService.open({
            //appendTo: this.dialogContainerRef,
            content: PersistModelDialogComponent,
            minHeight: 200, minWidth: 250, width: 450, actionsLayout: 'end'
        });

        const instance = dialogRef.content.instance as PersistModelDialogComponent;
        instance.initialize(id, name, primary, displayOrder);

        dialogRef.result.subscribe((result: { [key: string]: string }) => {
            if (result.text === "Submit") {
                const value = instance.formGroup.value as IPersistStateModel;
                this.createOrUpdatePersistState(value);
            }
        });
    }

    async openPersistListDialog() {
        const dialogRef: DialogRef = this.dialogService.open({
            //appendTo: this.dialogContainerRef,
            content: PersistListDialogComponent,
            minHeight: 200, minWidth: 250, width: 850, actionsLayout: 'end',
            cssClass: "custom-list-dialog"
        });

        const instance = dialogRef.content.instance as PersistListDialogComponent;
        instance.title = this.primengConfig.getTranslation('title.manageViews');
        instance.gridData = await this.persistStateService.loadData({ persistType: this.helperModel.persistType });

        dialogRef.result.subscribe((result: any) => {
            if (result.mode === 'save')
                this.createOrUpdatePersistState(result.dataItem, true)
            if (result.mode === 'edit')
                this.openPersistModelDialog(result.dataItem.id, result.dataItem.name, result.dataItem.primary, result.dataItem.displayOrder)
            if (result.mode === 'delete')
                this.removePersistState(result.dataItem.id);
            if (result.mode === 'select')
                this.selectPesrsistState(result.dataItem.id);
            if (result.mode === 'primary')
                this.primaryPesrsistState(result.dataItem.id);
        });
    }

    createOrUpdatePersistState(persistState: IPersistStateModel, tokenChanged: boolean = false) {

        const state = { ...this.state };
        state.skip = 0;

        const getColumnType = (field: string) => {
            return this.columns.find((x) => x.field == field).columnType;
        };

        let columns: any = this.grid.columns.toArray().filter((item) => !(item['isCheckboxColumn'] === true));

        columns = columns.map((item) => {
            return <ColumnSettings>{
                field: item['field'],
                columnType: getColumnType(item['field']),
                width: item['width'],
                title: item['title'],
                class: item['class'],
                style: item['style'],
                filter: item['filter'],
                format: item['format'],
                filterable: item['filterable'],
                sortable: item['sortable'],
                groupable: item['groupable'],
                orderIndex: item['orderIndex'],
                hidden: item['hidden']
            };
        });

        const config: GridConfig = {
            state: state,
            columns: columns.filter(x => !!x.field),
            pager: this.pager,
            groupable: this.groupable,
            filterExpressions: this.filterExpressions
        };

        const token = JSON.stringify(config, getCircularReplacer());

        const params = {
            persistStateId: persistState.id,
            name: persistState.name,
            primary: persistState.primary,
            displayOrder: persistState.displayOrder
        };

        const body = {
            persistType: this.helperModel.persistType,
            token: token,
            tokenChanged: tokenChanged
        };

        this.loading = true;
        this.persistStateService.createOrUpdate(body, params)
            .then(() => {
                this.helperModel.token = token;
                this.mapGridConfig();
                this.getDataSource();
            })
            .catch((error: Error) => {
                Promise.reject(error);
            }).finally(() => this.loading = false);
    }

    removePersistState(persistStateId: number) {

        this.loading = true;
        this.persistStateService.remove(persistStateId)
            .then(() => {
                this.toastrService.success(this.primengConfig.getTranslation('message.deletionCompleted'));
            })
            .catch((error: Error) => {
                Promise.reject(error);
            }).finally(() => this.loading = false);
    }

    selectPesrsistState(persistStateId: number) {

        this.loading = true;
        this.persistStateService.select(persistStateId)
            .then((result) => {
                this.helperModel.token = result.token;
                this.mapGridConfig();
                this.getDataSource();
            })
            .catch((error: Error) => {
                Promise.reject(error);
            }).finally(() => this.loading = false);
    }

    primaryPesrsistState(persistStateId: number) {

        this.loading = true;
        this.persistStateService.primary(persistStateId)
            .catch((error: Error) => {
                Promise.reject(error);
            }).finally(() => this.loading = false);
    }

}
